'use client';
import cn from 'classnames';

import Link from 'next/link';

import SlickCarousel from '@/components/common/SlickCarousel/SlickCarousel';
import AspectRatioImage from '@/components/common/AspectRatioImage/AspectRatioImage';

import './HighlightCarousel.style.scss';

type HighlightCarouselImage = {
  image: string;
  imageAltText: string;
  imageHeight: number;
  imageWidth: number;
  link?: string;
};

type HighlightCarouselItemProps = {
  className?: string;
  desktop: HighlightCarouselImage;
  mobile: HighlightCarouselImage;
};

function createLink(content: JSX.Element, link?: string) {
  return link ? <Link href={link}>{content}</Link> : content;
}

function HighlightCarouselItem(props: HighlightCarouselItemProps) {
  const { className = '', desktop, mobile } = props;
  const {
    image: desktopImage,
    imageAltText: desktopImageAlt,
    imageHeight: desktopImageHeight,
    imageWidth: desktopImageWidth,
    link: desktopLink
  } = desktop;

  const desktopContent = (
    <AspectRatioImage
      className={cn('HighlightCarouselItem-img-desktop')}
      src={desktopImage}
      alt={desktopImageAlt}
      height={desktopImageHeight}
      width={desktopImageWidth}
    />
  );

  const {
    image: mobileImage,
    imageAltText: mobileImageAlt,
    imageHeight: mobileImageHeight,
    imageWidth: mobileImageWidth,
    link: mobileLink
  } = mobile;

  const mobileContent = (
    <AspectRatioImage
      className={cn('HighlightCarouselItem-img-mobile')}
      src={mobileImage}
      alt={mobileImageAlt}
      height={mobileImageHeight}
      width={mobileImageWidth}
    />
  );

  return (
    <div className={cn('HighlightCarouselItem', className)}>
      {createLink(desktopContent, desktopLink)}
      {createLink(mobileContent, mobileLink)}
    </div>
  );
}

type HighlightCarouselProps = {
  className?: string;
  desktopItems: HighlightCarouselImage[];
  mobileItems: HighlightCarouselImage[];
};

function HighlightCarousel(props: HighlightCarouselProps) {
  const { className = '', desktopItems, mobileItems } = props;

  {
    /* 
    Chrome's device emulator has navigator.maxTouchPoints as 1 and so we only want the class to change the height on real mobile devices. The height from this class calculates the available space with the browser navigation bar (safari/chrome) instead of ignoring it.

    https://stackoverflow.com/questions/28083715/how-to-detect-if-a-mobile-device-is-emulated-by-google-chrome#comment108747578_28084346

    react-device-detect really can't tell if it's a real device or if it's the emulator, anymore
  */
  }

  let isMobile: boolean = false;
  if (typeof navigator !== 'undefined') {
    isMobile = navigator.maxTouchPoints > 1;
  }

  return (
    <div className={cn('HighlightCarousel', className)}>
      <SlickCarousel
        slickSettings={{
          dots: true,
          arrows: false,
          autoplay: !isMobile,
          autoplaySpeed: 5000,
          lazyLoad: 'progressive'
        }}
      >
        {mobileItems.map((_, index) => {
          return (
            <HighlightCarouselItem
              key={index}
              desktop={desktopItems[index]}
              mobile={mobileItems[index]}
            />
          );
        })}
      </SlickCarousel>
    </div>
  );
}

export default HighlightCarousel;
